import React, { useCallback, useMemo } from 'react';

import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { cn } from '../../lib/utils';
import { Form, FormControl, FormField, FormItem } from '../../ui/form';
import Header from '../Header/Header';
import MfaInputOTP from '../InputOTP/MfaInputOTP';

import { ResendSection } from './ResendSection/ResendSection';
import { useVerificationStatus } from './use-verification-status.hook';
import { getMessageType, getStatusMessage } from './verification-form.utils';
import { VerificationStatus } from './verification-status.enum';
import VerificationButtons from './VerificationButtons';
import { MfaVerificationFormProps, MfaVerificationFormValues } from './VerificationForm.types';

const verificationSchema = z.object({
  code: z.string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d+$/, 'Code must contain only numbers')
});

const MfaVerificationForm: React.FC<MfaVerificationFormProps> = ({
  email,
  onVerify,
  onResend,
  cooldownEndsAtMs,
  isLoading = false,
  showVerifyButton = true,
  hideResend = false,
  attemptsUsed = 0,
  maxAttempts = 5,
  successMessageDurationMs = 1000,
  className
}) => {
  const { status, handleVerification, resetStatus } = useVerificationStatus({
    onVerify,
    successMessageDurationMs
  });

  const form = useForm<MfaVerificationFormValues>({
    resolver: zodResolver(verificationSchema),
    defaultValues: {
      code: ''
    }
  });

  const { watch, setValue } = form;
  const codeValue = watch('code');

  const verificationSubmitHandler = useCallback(
    async (data: MfaVerificationFormValues) => {
      await handleVerification(data.code);
    },
    [handleVerification]
  );

  const runAutoSubmitAfterOtpComplete = useCallback((_completeCode: string) => {
    void form.handleSubmit(verificationSubmitHandler)();
  }, [form, verificationSubmitHandler]);

  const otpOnComplete = useMemo(() => {
    if (showVerifyButton) {
      return undefined;
    }
    return runAutoSubmitAfterOtpComplete;
  }, [showVerifyButton, runAutoSubmitAfterOtpComplete]);

  const handleResend = useCallback(async () => {
    await onResend();
    setValue('code', '');
    resetStatus();
  }, [onResend, setValue, resetStatus]);

  const handleCodeChange = useCallback((value: string) => {
    setValue('code', value);
    if (showVerifyButton && status === VerificationStatus.ERROR) {
      resetStatus();
    }
  }, [setValue, showVerifyButton, status, resetStatus]);

  const isVerifyDisabled = useMemo(
    () => codeValue.length !== 6 || isLoading || status === VerificationStatus.VERIFYING,
    [codeValue.length, isLoading, status]
  );

  const statusMessage = useMemo(
    () => getStatusMessage(status, attemptsUsed, maxAttempts),
    [status, attemptsUsed, maxAttempts]
  );

  const messageType = useMemo(
    () => getMessageType(status),
    [status]
  );

  return (
    <div className={cn('w-full max-w-[435px] mx-auto', className)}>
      <Form {...form}>
        <form
          className="space-y-6 w-full mx-auto"
          onSubmit={form.handleSubmit(verificationSubmitHandler)}
        >
          <Header
            title="Enter verification code"
            subtitle={`We emailed a 6-digit code to ${email}`}
          />
          <div className="flex flex-col items-center gap-8">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex justify-center">
                    <MfaInputOTP
                      value={field.value}
                      onChange={handleCodeChange}
                      onComplete={otpOnComplete}
                      message={statusMessage}
                      messageType={messageType}
                      disabled={isLoading || status === VerificationStatus.VERIFYING}
                    />
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <div className="flex flex-col items-center gap-3">
            {showVerifyButton && (
              <VerificationButtons
                isVerifyDisabled={isVerifyDisabled}
                isLoading={isLoading || status === VerificationStatus.VERIFYING}
              />
            )}

            {!hideResend && (
              <ResendSection
                onResend={handleResend}
                cooldownEndsAtMs={cooldownEndsAtMs}
                disabled={isLoading || status === VerificationStatus.VERIFYING}
              />
            )}
          </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export { MfaVerificationForm };

